import {
  useEffect, useMemo, useState, useRef,
} from 'react';

import type { LunaClient } from '@vlabs/api-bindings/src';
import { apiContainer } from '@vlabs/api-bindings/src';
import qs from 'qs';
import { useLocation } from 'react-router-dom';

import type { TDataState } from './types';

export interface UseVideoFromQueryReturn {
  state: TDataState;
  videoSrc?: string;
  fileList?: FileList;
}

export function useVideoFromQuery(): UseVideoFromQueryReturn {
  const { search } = useLocation();
  const [state, setState] = useState<TDataState>('notAsked');
  const [videoSrc, setVideoSrc] = useState<string | undefined>();
  const [fileList, setFileList] = useState<FileList | undefined>();

  const lunaClient = (apiContainer.lunaClient as LunaClient);
  const attemptRef = useRef(0);

  useEffect(() => {
    const parsed = qs.parse(search, { ignoreQueryPrefix: true }) as {
      objectId?: string;
      filename?: string;
    };
    const { objectId } = parsed;
    if (!objectId) {
      setState('fetched');
      setVideoSrc(undefined);
      setFileList(undefined);
      return;
    }

    attemptRef.current = 0;
    let cancelled = false;
    const loadVideo = async (): Promise<void> => {
      if (cancelled) return;

      if (attemptRef.current === 0) {
        setState('loading');
      }

      if (attemptRef.current >= 3) {
        setState('failed');
        return;
      }

      try {
        const objectResp = await lunaClient.objects.get(objectId, {
          responseType: 'blob',
          params: { with_meta: 1 },
        });

        if (cancelled) return;

        const blob = objectResp.data as Blob;
        const filenameHeader = objectResp.headers['x-luna-meta-filename'];
        const fileName = filenameHeader ?? parsed.filename;

        const dataTransferFileList = new DataTransfer();
        dataTransferFileList.items.add(new File([blob], fileName, { type: blob.type }));

        const objectUrl = URL.createObjectURL(blob);
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          return;
        }

        setFileList(dataTransferFileList.files);
        setVideoSrc(objectUrl);
        setState('fetched');
      } catch (e) {
        if (cancelled) return;
        attemptRef.current += 1;
        loadVideo();
      }
    };

    loadVideo();

    return () => {
      cancelled = true;
    };
  }, [search]);

  return useMemo(() => ({
    state,
    videoSrc,
    fileList,
  }), [state, videoSrc, fileList]);
}
